import { purpleGradientTextStyles } from '#app/components/MembersOnlyMarketing/PurpleGradientText.js';
import { Text } from '#app/components/Primitives/Text.js';
import { TextStyles } from '#app/theme/textStyles.js';
import { type SparklesEmoji } from '@contra/icons/emojis';
import { type SparklesIcon } from '@contra/icons/icons';
import { ReactNode } from 'react';
import { css, type DefaultTheme, styled } from 'styled-components';

export type RectangularTagProps = {
  readonly ariaLabel?: string;
  readonly canInteract?: boolean;
  readonly cursorType?: 'auto' | 'default' | 'pointer';
  readonly Icon?: null | typeof SparklesEmoji | typeof SparklesIcon;
  readonly iconPlacement?: 'left' | 'right';
  readonly iconSize?: number;
  readonly label?: ReactNode;
  readonly shape?: 'default' | 'pill';
  readonly size?: 'large' | 'medium';
  readonly textStyle?: TextStyles;
  readonly textWeight?: 400 | 500 | 600;
  readonly textWrap?: boolean;
  readonly variant:
    | 'blue'
    | 'gray'
    | 'gray-dark'
    | 'gray-light'
    | 'green'
    | 'magic-gradient'
    | 'opaque'
    | 'purple'
    | 'purple-gradient'
    | 'red'
    | 'watermelon'
    | 'white'
    | 'yellow05'
    | 'yellow';
};

const getTagColors = (
  theme: DefaultTheme,
  variant: RectangularTagProps['variant'],
  canInteract: boolean,
) => {
  switch (variant) {
    case 'blue':
      return css`
        background-color: ${theme.colorsV2.blue05};
        color: ${theme.colorsV2.blue80};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.blue10};
              }
            `
          : undefined}
      `;

    case 'gray':
      return css`
        background-color: ${theme.colorsV2.gray20};
        color: ${theme.colorsV2.gray70};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.gray30};
              }
            `
          : undefined}
      `;
    case 'gray-dark':
      return css`
        background-color: ${theme.colorsV2.gray80};
        color: ${theme.colorsV2.gray40};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.gray90};
              }
            `
          : undefined}
      `;
    case 'gray-light':
      return css`
        background-color: ${theme.colorsV2.gray05};
        color: ${theme.colorsV2.gray80};
        border: 1px solid ${theme.colorsV2.gray30};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.gray30};
              }
            `
          : undefined}
      `;
    case 'green':
      return css`
        background-color: ${theme.colorsV2.green05};
        color: ${theme.colorsV2.green80};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.green10};
              }
            `
          : undefined}
      `;
    case 'magic-gradient':
      return css`
        background: linear-gradient(
          90deg,
          rgba(243, 202, 77, 0.2) 0%,
          rgba(255, 152, 154, 0.2) 42.88%,
          rgba(203, 171, 255, 0.2) 94.62%
        );

        ${canInteract
          ? css`
              &:hover {
                background: linear-gradient(
                  90deg,
                  rgba(243, 202, 77, 0.3) 0%,
                  rgba(255, 152, 154, 0.3) 42.88%,
                  rgba(203, 171, 255, 0.3) 94.62%
                );
              }
            `
          : undefined}
      `;
    case 'opaque':
      return css`
        background-color: transparent;
        color: ${theme.colorsV2.gray60};
        border: 1px solid #00000000;

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.gray05};
              }
            `
          : undefined}
      `;
    case 'purple':
      return css`
        background-color: #f0eefc;
        color: ${theme.colorsV2.brandPurple};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.brandLilac};
              }
            `
          : undefined}
      `;
    case 'purple-gradient':
      return css`
        background-color: #f0eefc;

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.brandLilac};
              }
            `
          : undefined}

        > p {
          ${purpleGradientTextStyles}
        }
      `;
    case 'red':
      return css`
        background-color: ${theme.colorsV2.red10};
        color: ${theme.colorsV2.red70};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.red20};
              }
            `
          : undefined}
      `;
    case 'watermelon':
      return css`
        background-color: ${theme.colorsV2.brandWatermelon};
        color: ${theme.colorsV2.white};

        ${canInteract
          ? css`
              &:hover {
                background-color: #ee4144;
              }
            `
          : undefined}
      `;
    case 'white':
      return css`
        background-color: ${theme.colorsV2.white};
        color: ${theme.colorsV2.gray60};
        border: 1px solid ${theme.colorsV2.gray30};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.gray05};
              }
            `
          : undefined}
      `;
    case 'yellow':
      return css`
        background-color: ${theme.colorsV2.yellow10};
        color: ${theme.colorsV2.yellow70};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.yellow20};
              }
            `
          : undefined}
      `;
    case 'yellow05':
      return css`
        background-color: ${theme.colorsV2.yellow05};
        color: ${theme.colorsV2.yellow70};

        ${canInteract
          ? css`
              &:hover {
                background-color: ${theme.colorsV2.yellow10};
              }
            `
          : undefined}
      `;
    default:
      throw new Error(`Invalid variant: ${variant}`);
  }
};

const TagContainer = styled.div<{
  $canInteract: boolean;
  $cursorType?: RectangularTagProps['cursorType'];
  $iconPlacement: 'left' | 'right';
  $shape: 'default' | 'pill';
  $size: 'large' | 'medium';
  $textWrap: boolean;
  $variant: RectangularTagProps['variant'];
}>`
  ${({
    $canInteract,
    $cursorType,
    $iconPlacement,
    $shape,
    $size,
    $textWrap,
    $variant,
    theme,
  }) => css`
    display: flex;
    flex-direction: ${$iconPlacement === 'left' ? 'row' : 'row-reverse'};
    align-items: center;
    justify-content: center;

    width: fit-content;
    height: fit-content;
    min-height: ${$size === 'medium' ? '20px' : '32px'};

    padding: ${$shape === 'pill'
      ? '6px 8px'
      : $size === 'medium'
        ? '2px 6px'
        : '8px 16px 8px 16px'};
    border-radius: ${$shape === 'pill' ? css`6px` : css`4px`};
    gap: 6px;

    cursor: ${$cursorType
      ? $cursorType
      : $canInteract
        ? css`pointer`
        : css`default`};

    ${getTagColors(theme, $variant, $canInteract)}

    ${$textWrap
      ? css`
          > svg {
            margin-top: 2px;
            margin-bottom: auto;
          }
        `
      : undefined};

    transition: background-color 0.2s ease-out;
  `}
`;

const TagText = styled(Text)<{
  $fontSize: null | number;
  $textWeight: NonNullable<RectangularTagProps['textWeight']>;
  $textWrap: boolean;
}>`
  ${({ $fontSize, $textWeight, $textWrap }) => css`
    ${$fontSize
      ? css`
          font-size: ${$fontSize}px;
        `
      : css``}
    font-weight: ${$textWeight};
    ${$textWrap
      ? undefined
      : css`
          white-space: nowrap;
        `};
  `}
`;

export const RectangularTag = ({
  ariaLabel,
  canInteract = false,
  cursorType,
  Icon,
  iconPlacement = 'left',
  iconSize = 12,
  label,
  shape = 'default',
  size = 'medium',
  textStyle = 'overlineRegular',
  textWeight = 400,
  textWrap = false,
  variant,
}: RectangularTagProps) => {
  return (
    <TagContainer
      $canInteract={canInteract}
      $cursorType={cursorType}
      $iconPlacement={iconPlacement}
      $shape={shape}
      $size={size}
      $textWrap={textWrap}
      $variant={variant}
      aria-label={ariaLabel}
    >
      {Icon ? <Icon size={iconSize} /> : null}
      {label ? (
        <TagText
          $fontSize={textStyle ? null : size === 'medium' ? 10 : 12}
          $textWeight={textWeight}
          $textWrap={textWrap}
          textStyle={textStyle}
        >
          {label}
        </TagText>
      ) : null}
    </TagContainer>
  );
};
